import PropTypes from 'prop-types';
import { BsBuilding, BsGeoAlt, BsPeople, BsCurrencyEuro, BsCalendar3 } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';

import { formatEurCompact } from '../../utils/formatters';

/**
 * Single preview company card.
 */
const PreviewCard = ({ company }) => {
	const category = Array.isArray(company.companyCategory) ? company.companyCategory[0] : company.companyCategory;

	const details = [
		category && { icon: BsBuilding, text: category, title: 'Branche' },
		company.addressCity && { icon: BsGeoAlt, text: company.addressCity, title: 'Standort' },
		company.employeeCount !== null &&
			company.employeeCount !== undefined && {
				icon: BsPeople,
				text: String(company.employeeCount),
				title: 'Mitarbeiter',
			},
		company.revenue !== null &&
			company.revenue !== undefined && {
				icon: BsCurrencyEuro,
				text: formatEurCompact(company.revenue),
				title: 'Umsatz',
			},
		company.foundingYear && { icon: BsCalendar3, text: String(company.foundingYear), title: 'Gegründet' },
	].filter(Boolean);

	return (
		<div className="p-3 bg-white border border-gray-200 rounded-lg">
			<p className="text-sm font-semibold text-text mb-1.5">{company.companyName || 'Unbekannt'}</p>
			<div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-bgBase-800">
				{details.map((d, i) => (
					<span key={i} className="flex items-center gap-1" title={d.title}>
						<d.icon className="w-3 h-3 flex-shrink-0" />
						{d.text}
					</span>
				))}
			</div>
		</div>
	);
};

/**
 * Preview cards section showing 3 sample results.
 */
const AgenticPreviewCards = ({ companies, onApprove, onRefine, isExecuting }) => {
	if (!companies || companies.length === 0) {
		return (
			<div className="ml-8 p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm text-caution-700">
				Keine Unternehmen gefunden. Versuchen Sie, Ihre Kriterien anzupassen.
			</div>
		);
	}

	return (
		<div className="space-y-2 ml-8">
			{companies.map((company) => (
				<PreviewCard key={company.id} company={company} />
			))}
			<div className="flex gap-2 pt-1">
				<Button onClick={onApprove} size="sm" className="flex-1" loading={isExecuting}>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						className="w-3.5 h-3.5"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<path d="M7 10v12" />
						<path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z" />
					</svg>
					{isExecuting ? 'Wird gestartet...' : 'Ja, passt \u2013 alle laden'}
				</Button>
				<Button onClick={onRefine} disabled={isExecuting} variant="outline" size="sm" className="flex-1">
					<svg
						xmlns="http://www.w3.org/2000/svg"
						className="w-3.5 h-3.5"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<path d="M17 14V2" />
						<path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z" />
					</svg>
					Nein, anpassen
				</Button>
			</div>
		</div>
	);
};

PreviewCard.propTypes = {
	company: PropTypes.shape({
		id: PropTypes.string,
		companyName: PropTypes.string,
		companyCategory: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
		addressCity: PropTypes.string,
		employeeCount: PropTypes.number,
		revenue: PropTypes.number,
		foundingYear: PropTypes.number,
	}).isRequired,
};

AgenticPreviewCards.propTypes = {
	companies: PropTypes.array.isRequired,
	onApprove: PropTypes.func.isRequired,
	onRefine: PropTypes.func.isRequired,
	isExecuting: PropTypes.bool,
};

export default AgenticPreviewCards;
